import * as pr from 'preact'
import * as prc from 'preact/compat'
import * as sr from './shims/react.mjs'
import * as a from '@mitranim/js/all.mjs'
import * as ob from '@mitranim/js/obs.mjs'
import * as p from '@mitranim/js/prax.mjs'
import * as dg from '@mitranim/js/dom_global'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as rc from 'react-change'
import * as ii from 'internal/iso'
import * as c from './conf.mjs'
import * as um from './util-misc.mjs'
import * as ut from './util-text.mjs'
import * as ud from './util-dom.mjs'
import * as un from './util-net.mjs'
import * as uc from './util-coll.mjs'
import * as ue from './util-err.mjs'
import * as ul from './util-log.mjs'

/*
Short for "renderer". Provides various shortcuts for creating and mutating DOM
elements. In browsers, uses the native DOM API. In non-browsers, uses a DOM
shim. The shim supports serialization via `.outerHTML`, which is used for
server-side rendering.
*/
export const ren = new p.Ren(dg.global.document)

/*
Determines if we're rendering in Prax mode (default = false) or React mode.
The generic rendering function `E` uses this dynamic variable to determine
whether to generate DOM elements (Prax rendering mode) or React markup
drafts / vnodes (React rendering mode).

React-based rendering is deprecated. Newer code should be written for Prax.
*/
export const renderModeReactDyn = new a.Dyn(false)

/*
Wrapper for `pr.render` which switches our rendering system into React mode.
For the duration of this call, all calls to `E` will generate Preact vnodes.

Technical note. Preact's `render` function purports to support two modes:
replace all children (2-argument mode) and replace specific child (3-argument
mode). Due to complexities of the SSR/SPA hybrid, for our purposes, only the
all-child-replacing version is appropriate. Callers must be aware that this
replaces only the children, without affecting the properties and attributes
of the target DOM node.
*/
export function preactRender(tar, chi) {
  const prev = renderModeReactDyn.swap(true)
  try {return pr.render(chi, tar)}
  finally {renderModeReactDyn.swap(prev)}
}

/*
This allows re-renders of React view components, caused by `.setState` and
`.forceUpdate`, to properly work with our dynamic switching of rendering mode
via `.renderModeReactDyn`. Without this, Preact would invoke the `.render`
methods of our view components without switching rendering mode from Prax to
React, and as a result, `.render` methods would try to generate DOM elements
instead of React vnodes.

Usage:

  import * as pr from 'preact'
  pr.options.debounceRendering = u.preactScheduleRendering

Uses `setTimeout` because that's the implicit default in Preact.
*/
export function preactScheduleRendering(flush) {
  setTimeout(function preactFlushRendering() {
    const prev = renderModeReactDyn.swap(true)
    try {flush()}
    finally {renderModeReactDyn.swap(prev)}
  })
}

export function E(type, props, ...chi) {
  /*
  At the time of writing, on the author's machine, this costs somewhere between
  200ms and 400ms for various pages. This must never be used on production, but
  should be somewhat acceptable for testing.
  */
  if (c.ENABLE_TEST_ATTRS && !c.PUBLIC_RELEASE) {
    const tar = p.deref(props)
    if (a.isObj(tar) && !(`data-test` in tar)) {
      props = A.with(props).testAutoOpt(1, a.onlyFun(type)?.name)
    }
  }

  if (renderModeReactDyn.get()) return sr.E(type, p.deref(props), ...chi)
  return praxAdapterE(type, props, ...chi)
}

// Must be used for rendering SVG elements (instead of `E`).
export const S = ren.elemSvg.bind(ren)

/*
Adapter for migrating React-style markup to Prax. Wraps a Prax rendering
function to provide support for React-style props, and for passing a function
as the first argument.

Intended for React-style producer code and Prax consumer code.
Always returns DOM elements (shimmed in SSR, native in browsers).

TODO better name.

TODO migrate our code to Prax proper, and remove this.
*/
function praxAdapterE(type, props, ...chi) {
  if (a.isFun(type)) {
    if (isViewCls(type)) {
      throw TypeError(`component ${a.show(type)} is a React class and cannot be used in Prax markup`)
    }
    return type(reactPropsChiToJustReactProps(props, ...chi))
  }

  // TODO: instead of deleting incompatible props, throw exceptions.
  // All of our production markup should have been converted already.
  reactPropsDeleteIncompatible(props)

  chi = reactPropsToPraxChi(props, ...chi)

  /*
  Allows the callers to use `E` to mutate only the element's properties and
  attributes, without modifying its children. Seems like a somewhat common
  and intuitive use case.

  TODO drop after updating `@mitranim/js` (which will have this built-in).
  */
  if (a.isElement(type) && !chi.length) return ren.mutProps(type, props)

  return ren.elemHtml(type, props, ...chi)
}

export function reactPropsChiToJustReactProps(props, ...chi) {
  props = a.optStruct(p.deref(props))

  if (!a.vac(chi)) return props
  if (!props) return {children: chi}

  const propsChi = props.children
  props.children = a.isNil(propsChi) ? chi : uc.alwaysList(propsChi).concat(chi)
  return props
}

/*
TODO better name. This function extracts children from props for compatibility
with Prax, and removes those extracted properties from the props to avoid
redundancy and errors.
*/
function reactPropsToPraxChi(props, ...chi) {
  props = a.optStruct(p.deref(props))
  if (!props) return chi

  const propsHtml = props.dangerouslySetInnerHTML?.__html
  delete props.dangerouslySetInnerHTML

  const propsChi = props.children
  delete props.children

  // Avoid list allocation in some common cases.
  if (!a.vac(propsHtml) && !a.vac(propsChi)) return chi

  // We could concatenate children, but this is simpler and probably more
  // efficient.
  return [
    a.vac(propsHtml) && new p.Raw(propsHtml),
    propsChi,
    chi,
  ]
}

function reactPropsDeleteIncompatible(props) {
  props = p.deref(props)
  if (!a.isObj(props)) return

  if (`ctx` in props) delete props.ctx
  if (`ref` in props) delete props.ref
  if (`key` in props) delete props.key

  for (const key in props) {
    if (key.startsWith(`on`) && ut.isCodePointAsciiLetterUpper(key.codePointAt(2))) {
      delete props[key]
    }
  }
}

/*
Intended for React component classes. Differences:

  * `render` receives the component instance as the first argument.
  * Gets `ctx` from React context.
  * Automatically compares old/new props and state, rejects unnecessary updates.

This is called "inert" to differentiate from `MixReactView` which supports
observable-based reactivity.
*/
export function MixInertReactView(val) {return MixInertReactViewCache.goc(val)}

class MixInertReactViewCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixInertReactViewCls extends cls {
      // This property tells React that this component's `.context` should be
      // inherited from ancestor `ViewContext.Provider`. When rendering, we
      // always provider `ViewContext.Provider` at the top level, passing our
      // own `Ctx` instance through React context. This allows the `.ctx`
      // getter to work. See `withCtx` which is used at the top level.
      static get contextType() {return ViewContext}

      // Wraps/enhances the `.render` method.
      render = viewComponentRender

      // Accessing `ctx` and `env` from context is so common that it deserves a
      // shortcut.
      get ctx() {return this.context.ctx}
      get env() {return this.ctx?.env}
      get ctxLatest() {return this.env?.pending ?? this.env?.ctx ?? this.ctx}

      // Pay the cost of deeply comparing props in order to minimize unnecessary
      // re-renders.
      shouldComponentUpdate() {
        return rc.shouldComponentUpdate.apply(this, arguments)
      }
    }
  }
}

export const InertView = MixInertReactView(pr.Component)

/*
Enhanced version of `MixInertReactView`. Additional features:

  * Implicit reactivity for observables.
  * Automatic `.deinit` of owned resources on unmount:
    * Deinit own properties of `this`.
    * Deinit own properties of `this.state`.
    * Useful for stateful things such as timers.
  * Adds `this.isConnected` which indicates mounted/unmounted status.
*/
export function MixReactView(val) {return MixReactViewCache.goc(val)}

class MixReactViewCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixReactViewCls extends MixInertReactView(cls) {
      // Invoked by `viewComponentRenderReactive`.
      rec = new ob.Moebius(this)

      // Invoked by React.
      render = viewComponentRenderReactive
      componentWillMount = viewComponentWillMount
      componentWillUnmount = viewComponentWillUnmount

      // Invoked by `Moebius`.
      run() {return viewComponentRender.call(this)}

      // Invoked by `Moebius`.
      trig() {this.forceUpdate()}

      // Compatibility adapter for code that instantiates our component classes
      // directly, bypassing the Preact machinery. In Preact, `.state` is always
      // set to non-nil by the framework before invoking render methods, if this
      // property wasn't populated in the constructor. Some of our code relies on
      // Preact's "smart" behavior.
      get state() {return this.state = {}}
      set state(val) {a.pub(this, `state`, val)}
    }
  }
}

export const View = MixReactView(pr.Component)

/*
Changes the signature of the `.render` method of view components, by passing
the receiving object (`this`) as the first argument, which allows convenient
destructuring.
*/
function viewComponentRender() {
  const prev = renderModeReactDyn.swap(true)

  try {
    /*
    This uses the method `.render` from the prototype, rather than from the
    instance, because in the component constructor, we reassign `.render` to
    implement our wrapping/enhancement. Without this trick, we would recur
    infinitely / overflow the call stack.
    */
    // eslint-disable-next-line no-invalid-this
    return Object.getPrototypeOf(this).render.call(this, this)
  }
  finally {
    renderModeReactDyn.swap(prev)
  }
}

/*
Implementation note. `.rec` is an instance of `ob.Moebius`. When we invoke
`ob.Moebius..run`, it sets up an invisible context for registering observable
subscriptions, and then calls our method `.run`. See `MixReactView` for the
implementation of `.run`, which finally calls the underlying rendering method
of the given view component. This indirect calling allows our view components
to register observable subscriptions automatically and invisibly.
*/
function viewComponentRenderReactive() {
  // eslint-disable-next-line no-invalid-this
  return this.rec.run(this)
}

function viewComponentWillMount() {
  // eslint-disable-next-line no-invalid-this
  this.isConnected = true

  // eslint-disable-next-line no-invalid-this
  Object.getPrototypeOf(this).componentWillMount?.call(this)
}

/*
Performs cleanup and calls `componentWillUnmount` of the current subclass.
Allows subclasses to simply define this method without worrying about calling
`super.componentWillUnmount()`.
*/
function viewComponentWillUnmount() {
  /* eslint-disable no-invalid-this */

  this.isConnected = false

  this.rec.deinit()

  // Deinits any properties that implement the `a.isDe` interface.
  ob.deinitAll(this)

  ob.deinitAll(this.state)

  Object.getPrototypeOf(this).componentWillUnmount?.call(this)

  /* eslint-enable no-invalid-this */
}

export class EagerView extends View {
  shouldComponentUpdate() {return true}
}

// Automatically binds all methods. Opt-in because of measurable overhead.
export class BoundView extends View {
  constructor() {
    super(...arguments)
    um.bindAll(this)
  }
}

export const ViewContext = pr.createContext({ctx: undefined})

export function withCtx(ctx, chi) {
  chi = uc.unwrapList(chi)
  if (!a.vac(chi)) return undefined

  // Avoid redundant wrapping.
  if (getVirtualType(chi) === ViewContext.Provider) return chi

  return sr.E(ViewContext.Provider, {value: {ctx}}, chi)
}

export function isViewInstance(value) {
  return a.isObj(value) &&
    a.isFun(value.setState) &&
    a.isFun(value.forceUpdate)
}

export function firstVirtualChild(children) {
  return uc.alwaysHead(children)
}

export function isVirtualElement(value) {
  return pr.isValidElement(value)
}

export function isVirtualPrintable(value) {
  return ut.isPrintable(value) || isVirtualElement(value)
}

export function getVirtualType(value) {
  if (isVirtualElement(value)) return value.type
  return undefined
}

export function getVirtualProps(value) {
  if (isVirtualElement(value)) return value.props
  return undefined
}

export function getVirtualKey(value) {
  if (isVirtualElement(value)) return value.key
  return undefined
}

export function isViewCls(val) {
  return a.isCls(val) && a.hasMeth(val.prototype, `render`)
}

export function isViewFun(val) {
  return a.isFun(val) && !isViewCls(val)
}

export async function viewComponentToVirtualElement(ctx, comp, props) {
  a.reqObj(ctx)
  a.reqFun(comp)
  a.optStruct(props)

  await comp.prefetch?.(ctx)

  if (isViewCls(comp)) return sr.E(comp, props)

  const prev = renderModeReactDyn.swap(true)
  try {return comp(ctx, props)}
  finally {renderModeReactDyn.swap(prev)}
}

export function preactClear(node) {preactRender(node, undefined)}

export const targetBlank = Object.freeze({
  target: `_blank`,
  rel: `noopener noreferrer`,
})

// Note: should return a dict even if empty, allowing to reset previous value.
export function bgUrlStyle(url) {
  return {backgroundImage: bgUrl(url)}
}

/*
Note: if the URL contains spaces or other non-URL characters, it must be
URL-encoded before calling this function. We can't encode them indiscriminately,
because that would wreck some valid URLs.
*/
export function bgUrl(val) {
  return (val = a.renderLax(val)) && `url(${JSON.stringify(val)})`
}

export function dataTest(val) {
  return (c.ENABLE_TEST_ATTRS && val) || undefined
}

export function testProps(val) {
  val = dataTest(val)
  return val && {'data-test': val}
}

/*
Semi-placeholder, preserved for migrating old code. In the new rendering system,
the initial markup cannot pass callbacks. TODO revise.

Old readme:

---

Usage:

  <span {...u.fakeButtonProps({onClick: someAction})}>
    label
  </span>

Usable for layouts unsupported by the native button element, such as a flex
container. Doesn't work for submit buttons. Supports focus and keyboard
activation. Last resort, try native buttons first.
*/
export function fakeButtonProps({onClick, disabled, readOnly, ...props} = {}) {
  disabled = a.laxBool(disabled || readOnly)
  return {
    role: `button`,
    tabIndex: disabled ? undefined : 0,
    onKeyPress: (onClick && !disabled)
      ? a.bind(ud.simulateActivationOnKeypress, onClick)
      : undefined,
    onClick,
    disabled,
    ...props,
  }
}

export function markerStr(val) {return val ? `` : undefined}

export function linkCanonOpt(path) {
  path = a.renderLax(path)
  return path ? linkCanon(path) : undefined
}

function linkCanon(path) {
  return E(`link`, {
    rel: `canonical`,
    href: ut.siteUrl(path) || c.SERVER_ORIGIN,
  })
}

export function findDomNode(val) {return prc.findDOMNode(val)}

// TODO consolidate with `NonEmpty` and `OptBlock`.
// TODO better name.
export function nonEmptyE(type, props, ...children) {
  if (!a.vac(children)) return null
  return E(type, props, ...children)
}

/*
Reference: https://www.w3.org/TR/wai-aria-1.1/#aria-current

Tentative. Hardcodes 'true', which is not always the right value for this
attribute.
*/
export function cur(val) {return val ? {'aria-current': `true`} : undefined}

// Corresponds to `cs-core` type `UiView`.
export function isUiView(val) {
  return a.isStruct(val) && a.isPk(val.type)
}

export function switchLang(lang) {
  const ctx = ud.getCtx()
  if (!lang || lang === ctx.lang) return undefined

  ctx.env.setLang(lang)
  ctx.env.dat.clear()
  return ctx.env.updateCurrentPage().catch(um.onUnhandledErr)
}

// TODO more specific name.
export function flat(val) {
  return a.flat(a.isSeq(val) ? val : [val]).filter(a.isSome)
}

export function lenDeep(val) {return flat(val).length}

export function mapDeep(val, fun) {return flat(val).map(fun)}

export function cls(...val) {return a.spaced(...a.compact(val))}

export function MixNode(val) {return MixNodeCache.goc(val)}

export class MixNodeCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixNodeCls extends a.MixChildCon(a.MixNode(cls)) {
      // Shortcut for instance method `.init`, see below.
      static init(...val) {return new this().init(...val)}

      // Override in subclass.
      init() {return this}

      /*
      These are defined as getters, rather than methods, for consistency with
      React-based `View`. TODO convert to methods everywhere.
      */
      get env() {return this.ctx?.env || ud.getEnv()}
      get ctx() {return this.parentNode?.ctx || ud.getCtx()}

      pageDat() {return this.ctx.pageDat}
      buk() {return this.pageDat()?.buk}
      bank() {return this.env.disk.bank()}
      vac() {return this.hasChildNodes() ? this : undefined}
    }
  }
}

export function MixElem(val) {return MixElemCache.goc(val)}

export class MixElemCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixElemCls extends MixNode(cls) {
      // TODO: `@mitranim/js/prax.mjs`.`Ren` should support prepending.
      prependChi(...val) {
        ud.prependChild(this, ren.node(...val))
        return this
      }

      appendChi(...val) {
        for (val of val) ren.appendChi(this, val)
        return this
      }

      prependSibling(val) {return ud.prependSibling(this, val)}
      appendSibling(val) {return ud.appendSibling(this, val)}
      onlyEmpty() {return a.vac(!this.hasChildNodes()) && this}

      /*
      Caution. This method is a trap, of sorts. On the client, this should be
      executed only when the element is connected to the DOM, and we should run
      `preactClear` when the element is disconnected from the DOM. Executing
      this on disconnected elements or forgetting to use `preactClear` can lead
      to subscription leaks.

      Whenever possible, use `MixElemReactToPrax` which correctly handles
      cleanup.
      */
      reactChi(...val) {
        return ii.renderReactChildrenIntoDomElement(this.ctx, this, val)
      }

      // Experimental. The given class must conform to our common element
      // interface, using `MixNode` and implementing `.init`.
      idemp(cls, opt) {
        if (a.isNil(cls)) return undefined
        return this.desc(cls) || new cls(this).init(opt)
      }

      hasDesc(cls) {return !!this.desc(cls)}

      // TODO better name. This creates a potential sibling, without attaching
      // it to the parent.
      sprout() {return new this.constructor(this.parentNode)}

      // Returns a proxy for `.dataset` with additional sanity features such
      // as type restrictions.
      data() {return dataset(this)}

      // Useful for element classes that require non-primitive data, the kind
      // that can't be conveniently serialized through data-attributes.
      // TODO rename to `getDataProps` and `setDataProps`.
      getProps() {return a.jsonDecode(this.dataset.props)}

      setProps(val) {
        this.data().props = ut.jsonEncodeSome(val)
        return this
      }
    }
  }
}

// Popular superclass for custom elements.
export const Elem = MixElem(dg.global.HTMLElement)

export class Frag extends MixNode(dg.global.DocumentFragment) {}

export function MixElemReinit(cls) {
  return class MixElemReinitCls extends MixElemCtxSub(cls) {
    async reinit(...src) {
      this.ctxReinit()
      await this.preinit(...src)
      await this.pageDat().refresh()
      await this.init()
      return this
    }
  }
}

/*
For elements that want to support cancelable / restartable network requests.
Creating a new sub-context cancels any requests on the previous sub-context.
Note that this is not needed for elements that don't allow overlapping network
requests, such as forms or buttons that are blocked for the duration of their
request.
*/
export function MixElemCtxSub(cls) {
  return class MixElemCtxSubCls extends cls {
    get ctx() {return this.ctxSub ?? super.ctx}
    ctxBase() {return super.ctx}
    ctxInit() {return this.ctxSub ??= this.ctxBase().sub()}

    ctxReinit() {
      this.ctxSub?.deinit()
      this.ctxSub = undefined
      return this.ctxInit()
    }

    // For custom DOM elements.
    disconnectedCallback() {
      try {super.disconnectedCallback?.()}
      finally {this.ctxSub?.deinit()}
    }

    // For React view components.
    componentWillUnmount() {
      try {super.componentWillUnmount?.()}
      finally {this.ctxSub?.deinit()}
    }
  }
}

export function dataset(val) {
  return new Proxy(a.reqElement(val).dataset, datasetPh)
}

/*
Proxy handler for dataset objects. Should be used to wrap the `.dataset`
property of HTML elements. Provides additional type checks, and supports
deleting dataset properties when their value is set to nil. This allows
operations on `.dataset` to be more consistent with how `@mitranim/js/prax.mjs`
handles props / attributes.

TODO consider moving to `@mitranim/js/prax.mjs`.
*/
const datasetPh = a.Emp()

datasetPh.set = function set(tar, key, val) {
  if (a.isNil(val)) delete tar[key]
  else tar[key] = a.renderLax(val)
  return true
}

/*
Short for "props builder". Provides shortcuts for building view props, by
mutating an underlying dict which can be dereferenced via `.$`. Support for
automatically dereferencing props builders is built into
`@mitranim/js/prax.mjs` and our own `shims/react.mjs`.
*/
export class PropBui extends p.PropBui {
  /*
  TODO: remove `.onkeypress` from here. It's inertia from React props and SPA
  thinking.
  */
  fakeBtn() {
    return this
      .role(`button`)
      .tabIndex(0)
      .onkeypress(a.vac(c.DOM_AVAILABLE) && fakeBtnOnClick)
  }

  /*
  Caution: target element must also attach event listeners by calling
  `PropBui.fakeLink`. Normally this is done in `.connectedCallback`.
  */
  fakeLink(href) {
    if (!(href = a.renderLax(href))) return this

    return this
      .role(`link`)
      .tabIndex(0)
      .set(`data-href`, href)
  }

  static fakeLink(tar) {
    if (!c.DOM_AVAILABLE) return
    a.reqElement(tar)
    tar.onclick = fakeLinkOnClick
    tar.onkeydown = fakeLinkOnKeydown
  }

  clsDefault(val) {return this.get(`class`) ? this : this.cls(val)}

  bgUrl(src) {
    const style = bgUrlStyle(src)
    return style ? this.style(style) : this
  }

  test(name) {
    a.optStr(name)
    if (!c.ENABLE_TEST_ATTRS || !name) return this
    return this.set(`data-test`, name).delete(`data-test-auto`)
  }

  // Compare `clsDefault`.
  testDefault(name) {
    a.optStr(name)
    if (this.hasTestManual()) return this
    return this.test(name)
  }

  /*
  Usage in views:

    A.testFrom(this)

  The input may be either a view component instance or a view component class.
  */
  testFrom(val) {
    a.reqComp(val)
    return this.test(a.onlyValidStr(val.name) || a.onlyValidStr(val.constructor?.name))
  }

  testAuto(off) {
    off = a.laxNat(off)

    if (!c.ENABLE_TEST_ATTRS) return this
    if (this.hasTestManual()) return this

    const name = um.callerFuncNameTitle(off + 2)
    if (!name) return this

    return this.set(`data-test`, name).set(`data-test-auto`, ``)
  }

  testAutoOpt(off, name) {
    off = a.laxInt(off)
    a.optStr(name)
    return name ? this.test(name) : this.testAuto(off + 1)
  }

  hasTest() {return this.has(`data-test`)}
  hasTestAuto() {return this.has(`data-test-auto`)}
  hasTestManual() {return this.hasTest() && !this.hasTestAuto()}

  reactInnerHtml(val) {return this.set(`dangerouslySetInnerHTML`, {__html: val})}

  /*
  Enables support for nil values in input props. By default, Preact ignores
  them; we want them to reset the input state.

  If we ever remove Preact, this should be dropped.
  */
  valueNorm() {
    if (this.has(`value`) && a.isNil(this.get(`value`))) return this.value(``)
    return this
  }

  // See `readme.md` → "SSR interactivity gotcha".
  // Also see the function `enableAfterSsr` which does the reverse.
  // TODO: disable only fieldsets instead of actual controls.
  ssrDisabled() {
    if (!c.DOM_AVAILABLE && !this.has(`disabled`)) {
      return this.disabled(true).set(`data-enable`, ``)
    }
    return this
  }

  tooltip(val) {
    return ((val = a.renderLax(val))) ? this.set(`aria-label`, val) : this
  }

  tooltipPos(val) {return this.set(`data-label-pos`, val)}

  sortDesc(desc) {
    if (a.isNil(a.optBool(desc))) return this
    return this.set(c.ARIA_SORT, (desc ? c.ARIA_SORT_DESC : c.ARIA_SORT_ASC))
  }

  // TODO consolidate name with `MixElem..setProps`.
  dataProps(val) {return this.set(`data-props`, ut.jsonEncodeSome(val))}

  // Similar to `markerStr`. TODO better name.
  marker(key, val) {return val ? this.set(key, ``) : this}

  /*
  Questionable signature. This is a non-boolean attribute with many possible
  values, such as `true`, `page`, and more. The string `true` is not always
  the most appropriate. We could relax the input to `bool | string`, which
  is also questionable.
  */
  ariaCur(val) {
    return this.set(`aria-current`, a.vac(a.laxBool(val)) && `true`)
  }

  title(val) {return this.set(`title`, val)}
  alt(val) {return this.set(`alt`, val)}

  /*
  TODO better name. Similar to `.is`, but instead of taking a string, takes
  either a custom DOM element instance (shimmed or native), or a custom DOM
  element class. Relies on the static property `.customName` which is
  usually set by a call to `dr.reg` in our custom elements. Note that this
  property is non-standard. This is a feature of the custom element class
  registration system provided by Prax.

  TODO remove this and directly instantiate DOM element classes instead.
  */
  isFrom(val) {
    if (!a.optComp(val)) return this

    const customName = val.customName ?? val.constructor.customName
    if (!customName) return this

    const localName = val.localName ?? val.constructor.localName
    if (localName === customName) return this

    return this.is(customName)
  }

  /*
  static setIdemp(tar, key, val) {
    a.reqValidStr(key)

    if (a.isInst(tar, p.PropBui)) {
      if (a.is(tar.get(key), val)) return tar
      return tar.set(key, val)
    }

    if (!a.isObj(tar)) return {[key]: val}
    tar[key] = val
    return tar
  }
  */

  /*
  Hack for supporting `Btn..validateType`. This ensures that Prax always sets
  the attribute `[submit]` on buttons, even when rendering native DOM on the
  client. Our `Btn..validateType` detects whether `[type=submit]` is
  intentional by checking the presence of the attribute, differentiating this
  from "accidental" `[type=submit]` which is the default when the attribute is
  not present. The reason this hack is required is because Prax is "too smart":
  when rendering in the native DOM, when copying this property from the provided
  props, it checks the previous value of the `.submit` property on the native
  button element, and because the default value is the string `submit` identical
  to the value we're trying to set, Prax avoids modifying the property, thus
  failing to set the attribute. We opt out of this "smart" behavior by using the
  standard property `.attributes` specially supported by Prax.

  TODO consider if Prax should NOT have this "smart" behavior. It was
  implemented for performance reasons, to avoid layout recalculations
  from idempotent operations. Need benchmarks.
  */
  submit() {return super.submit().set(`attributes`, {type: `submit`})}
}

/*
Short for "attributes". Easier to type than "P".
Default/empty/starting/frozen instance of a props builder.
Empty "A" is equivalent to `{}` or `undefined`.
Calling any method creates a new mutable instance.
*/
export const A = new PropBui().frozen()

export function fakeBtnOnClick(eve) {
  a.reqEvent(eve)

  // See the explanation in `fakeLinkOnClick`.
  // eslint-disable-next-line no-invalid-this
  if (ud.eventHasDefault(eve, this)) return

  // TODO consider also supporting clicks.
  if (eve.key === c.KEY_ENTER || eve.key === c.KEY_SPACE) {
    a.eventKill(eve)
    // eslint-disable-next-line no-invalid-this
    this.onclick(eve)
  }
}

// Target element must have `data-href="..."` with the link.
export function fakeLinkOnClick(eve) {
  if (!a.optEvent(eve)) return

  /*
  Explanation. Our "fake links" often contain other controls, such as anchors
  or text inputs. Their native event behaviors run LAST, after custom listeners
  such as this one. Without this special case, "fake link" functionality would
  execute first, and prevent the default, which is undesirable in such cases.
  */
  // eslint-disable-next-line no-invalid-this
  if (ud.eventHasDefault(eve, this)) return

  // eslint-disable-next-line no-invalid-this
  const href = this.dataset.href
  if (!href) return

  /*
  For comparison, YouTube's fake links do not handle modified clicks.
  They haven't fixed it in YEARS.
  */
  if (eve.ctrlKey || eve.metaKey) {
    a.eventKill(eve)
    window.open(href, `_blank`)
    return
  }

  if (a.isEventModified(eve)) return
  a.eventKill(eve)

  const {origin} = ut.url(href)

  if (origin && origin !== window.location.origin) {
    ud.hardRedirect(href)
  }
  else {
    ud.getEnv().locPush(href)
  }
}

// TODO handle modifiers for consistency with native links.
export function fakeLinkOnKeydown(eve) {
  if (eve?.key === c.KEY_ENTER) {
    // eslint-disable-next-line no-invalid-this
    (this.onclick || fakeLinkOnClick).call(this, eve)
  }
}

// Try to refresh page once if no previous refresh was made.
export function tryRefreshOnce() {
  if (!c.DOM_AVAILABLE || !c.PROD) return

  const query = new ut.Query(window.location.search)
  if (query.has(c.QUERY_KEY_FRESH)) return

  query.set(c.QUERY_KEY_FRESH, ``)
  window.location.search = query
}

export function makeGalleryId(id) {return a.str(`gallery_`, id)}

export function MixCycler(cls) {
  return class MixCyclerCls extends cls {
    get CyclerChild() {throw ue.errImplementInSubclass()}

    // Override in subclass if necessary.
    cyclerTransition(prev, next) {
      prev?.setCurrent(false)
      next?.setCurrent(true)
      return this
    }

    cyclerDescs() {return this.descs(this.CyclerChild)}
    cyclerCurrent() {return a.find(this.cyclerDescs(), ud.isCurrent)}
    cyclerPrev() {return this.cyclerShift(-1)}
    cyclerNext() {return this.cyclerShift(1)}
    cyclerShowCurrent() {return this.cyclerCurrent()?.scrollIntoView(), this}

    cyclerShift(off) {
      const descs = a.arr(this.cyclerDescs())
      const ind = a.findIndex(descs, ud.isCurrent)
      const prev = descs[ind]
      const next = prev ? um.arrCycle(descs, ind, off) : a.head(descs)
      this.cyclerTransition(prev, next)
      return this
    }
  }
}

export function MixCyclerChild(cls) {
  return class MixCyclerChildCls extends cls {
    setCurrent(val) {return ren.mutProps(this, A.ariaCur(a.laxBool(val)))}
  }
}

/*
Base class for pages or page components which are implemented as stateful
objects rather than async functions. This approach can be more cumbersome
but also more flexible. This is a workaround for the inability to directly
instantiate components in React. In the custom DOM element API, this would
be an actual view class (custom DOM element class) rather than something
abstract.
*/
export class AbstractView extends um.MixCtxed(a.Emp) {
  constructor(ctx) {super().setCtx(ctx)}

  // Subclasses should override this.
  preinit() {}
  async init() {}
  async htmlHead() {}
  async htmlBody() {}

  // Callers should only have to call this.
  static async res(...src) {
    const tar = new this(...src)
    await tar.preinit()
    await tar.init()
    return tar.res()
  }

  async res() {
    const [head, body] = await um.plist()
      .add(this.htmlHead())
      .add(this.htmlBody())
    return un.resHtml({head, body})
  }
}

/*
TODO better naming. Intended for custom elements with complicated or expensive
client-side initialization which may be asynchronous and likely to fail.

Note: use `MixElemReactToPrax` for elements that use React for their children.
Correctly handling all cases involved in SSR, client-side init, and pushstate
transitions involves additional nuances and constraints handled by
`MixElemReactToPrax`, but not by this class.
*/
export function MixElemErrant(val) {return MixElemErrantCache.goc(val)}

class MixElemErrantCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixElemErrantCls extends cls {
      connectedCallback() {
        const tar = this.connectedCallbackAsync()
        this.env?.initDelay?.addOpt(tar)
      }

      async connectedCallbackAsync() {
        try {
          super.connectedCallback?.()
          await this.onConnected()
          return this.isConnected
        }
        catch (err) {
          await this.onErr(err)
          return false
        }
      }

      async onConnected() {}

      async onErr(err) {
        if (!err) return
        ul.maybeLogErr(err)
        const mod = await um.imp(import(`./views/errors.mjs`))
        E(this, {}, mod.FieldErrorText({error: err}))
      }
    }
  }
}

export class ElemForReactHeavyPage extends MixElemErrant(Elem) {
  onConnected() {return this.initedDataOnce()}

  async init() {
    await this.initedDataOnce()
    return this
  }

  async initedDataOnce() {
    if (this.inited) return false

    await this.initData()
    this.constructor.regElemCls()

    this.inited = true
    return true
  }

  // Override in subclass.
  initData() {}

  /*
  Must be used instead of `dr.reg` in every file that uses this class.
  Allows to delay registration of descendant custom element classes until
  we fully initialize this element's data.
  */
  static addElemCls(val) {(this.descs ??= []).push(ud.reqElemCls(val))}

  /*
  Flushes any pending custom element classes.
  Should be called after fully initializing this element's data.
  Stupid name, TODO revise.
  */
  static regElemCls() {this.descs?.splice(0).forEach(dr.reg)}
}

/*
Symptom of an antipattern carried over from React.

In React, it's traditional to use one structure called "props" to mix data
intended for different, incompatible consumers: DOM and our own view
components. This mixing can cause issues such as taking configuration props
intended for view components and accidentally turning them into DOM attributes.
Sometimes this leads to exceptions, and we're lucky if that's caught in
development. Sometimes this leads to bloating HTML with non-standard attributes
that should not be there.

This should be resolved by separating DOM props from non-DOM props. Any
configuration inputs intended for view components should be passed separately
from DOM props. This will avoid the need to unwrap `PropBui` to extract our own
configuration props.
*/
export function normProps(val) {return a.laxDict(p.deref(val))}

export function elemReuseById(id, props) {
  return elemReuse(ud.elemById(id), props)
}

export function elemReuse(tar, props) {
  return a.optElement(tar) && ElemReuse.init(tar, props)
}

/*
A trick for reusing a DOM element between prev and next page. When rendering the
next page with Prax, we could simply grab an existing element and use it as-is
in the new markup. But that would immediately remove it from the current page,
because DOM elements can have only one parent. Instead, we use this "dummy"
element and give it as a reference to the desired element. When connected, it
replaces itself.
*/
export class ElemReuse extends Elem {
  static {dr.reg(this)}

  target = undefined

  init(tar, props) {
    if (!a.optElement(tar)) return undefined
    if (!c.DOM_AVAILABLE) return tar

    this.target = tar
    this.hidden = true
    this.setProps(props)
    return this
  }

  connectedCallback() {
    const tar = this.target
    this.replaceWith(tar)
    tar.onReuse?.(this.getProps())
  }
}
